/**
 * Tiny `fetch` wrapper for the widget. We hit the **public** widget
 * endpoints exposed by the `tunisiaship_branding` plugin
 * (``/v1/tunisiaship/widget/*``). They:
 *
 *   * accept anonymous traffic (no API key shipped to the browser),
 *   * enforce a per-deployment ``Origin`` allow-list server-side,
 *   * return a redacted view of the underlying Karrio resource
 *     (no internal IDs, no org metadata, no carrier credentials).
 *
 * The widget therefore never needs a resource token nor an API key —
 * which is the only sane model for an embeddable JS bundle distributed
 * over a public CDN.
 */
declare const __TS_API_URL__: string;

export interface PublicTrackerResponse {
  tracking_number?: string;
  carrier_name?: string;
  status?: string;
  events?: Array<{ date?: string; time?: string; description?: string; location?: string; code?: string }>;
  estimated_delivery?: string | null;
  delivered?: boolean;
  info?: {
    carrier_tracking_link?: string;
    expected_delivery?: string;
    shipment_origin_country?: string;
    shipment_destination_country?: string;
    shipment_service?: string;
  };
}

export interface ProxyRatesResponse {
  rates?: Array<{
    service?: string;
    carrier_name?: string;
    total_charge?: number;
    currency?: string;
    transit_days?: number;
    estimated_delivery?: string;
  }>;
  messages?: Array<{ carrier_name?: string; message?: string }>;
}

export interface CarriersSummaryResponse {
  generated_at?: number;
  carriers?: Array<{
    carrier_name: string;
    display_name?: string;
    capabilities: string[];
    services: string[];
    in_incident?: boolean;
  }>;
}

const FALLBACK_API = 'https://api.ship.goodvibes.tn';

export function apiBase(override?: string): string {
  if (override) return override.replace(/\/$/, '');
  if (typeof __TS_API_URL__ === 'string' && __TS_API_URL__) return __TS_API_URL__.replace(/\/$/, '');
  return FALLBACK_API;
}

class WidgetApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response): Promise<WidgetApiError> {
  let detail = '';
  let code: string | undefined;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    code = body?.error?.code;
    detail = body?.error?.message ?? '';
  } catch {
    try {
      detail = await res.text();
    } catch {
      detail = '';
    }
  }
  return new WidgetApiError(res.status, `HTTP ${res.status} ${detail}`.trim(), code);
}

export async function fetchPublicTracker(
  apiUrl: string,
  carrierName: string,
  trackingNumber: string,
  signal?: AbortSignal,
): Promise<PublicTrackerResponse> {
  const url = `${apiUrl}/v1/tunisiaship/widget/tracking/${encodeURIComponent(carrierName)}/${encodeURIComponent(
    trackingNumber,
  )}`;
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    cache: 'no-store',
    signal,
    credentials: 'omit',
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as PublicTrackerResponse;
}

export interface RatesQuery {
  shipper: { postal_code?: string; city?: string; country_code?: string };
  recipient: { postal_code?: string; city?: string; country_code?: string };
  parcels: Array<{
    weight: number;
    length?: number;
    width?: number;
    height?: number;
    weight_unit: 'KG';
    dimension_unit: 'CM';
  }>;
}

export async function fetchRates(
  apiUrl: string,
  payload: RatesQuery,
  signal?: AbortSignal,
): Promise<ProxyRatesResponse> {
  const res = await fetch(`${apiUrl}/v1/tunisiaship/widget/rates`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
    },
    body: JSON.stringify(payload),
    cache: 'no-store',
    signal,
    credentials: 'omit',
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as ProxyRatesResponse;
}

export async function fetchCarriersSummary(
  apiUrl: string,
  signal?: AbortSignal,
): Promise<CarriersSummaryResponse> {
  const res = await fetch(`${apiUrl}/v1/tunisiaship/widget/carriers-summary`, {
    headers: { accept: 'application/json' },
    signal,
    credentials: 'omit',
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as CarriersSummaryResponse;
}

export { WidgetApiError };
